import { useRef, useState } from 'react';

interface SelectableItem {
  onSelect?: () => void;
}

/**
 * Wiring for a Button that toggles a Popover holding KDS Menus. Spread
 * `triggerProps` on the Button and `popoverProps` on the Popover. The
 * Popover's target is a ref, so it can't put `aria-haspopup` on the Button
 * itself; the trigger sets it to `menu` here. The Popover's content wrapper
 * gets a neutral role instead of its `dialog` default: the KDS Menu inside it
 * is already the `menu`, and a `menu` may only contain menu items or groups.
 */
export default function useMenuPopover() {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [visible, setVisible] = useState(false);

  const hide = () => {
    setVisible(false);
  };

  const toggle = () => {
    setVisible((prev) => !prev);
  };

  /** Wraps each item's `onSelect` so choosing it also closes the menu. */
  const closeOnSelect = <T extends SelectableItem>(items: T[]): T[] =>
    items.map((item) => ({
      ...item,
      onSelect: () => {
        hide();
        item.onSelect?.();
      },
    }));

  return {
    visible,
    hide,
    closeOnSelect,
    triggerProps: {
      ref: buttonRef,
      onClick: toggle,
      'aria-haspopup': 'menu' as const,
      'aria-expanded': visible,
    },
    popoverProps: {
      target: buttonRef,
      visible,
      onRequestHide: hide,
      placement: 'bottom-start' as const,
      manageEvents: false as const,
      contentRole: 'presentation',
    },
  };
}
